'use client';

import { Card, CardContent, CardAction } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ProviderLogo } from './ProviderLogo';
import { ConnectionStatus } from './ConnectionStatus';
import { formatRelativeTime } from '@/lib/integrations/utils';
import type { UserConnection } from '@/lib/integrations/types';
import { Trash2, RefreshCw, Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ConnectionCardProps {
  connection: UserConnection;
  providerLogo?: string;
  onDelete?: () => void;
  onRefresh?: () => void;
  onSetDefault?: () => void;
  deleting?: boolean;
  settingDefault?: boolean;
  className?: string;
}

export function ConnectionCard({
  connection,
  providerLogo,
  onDelete,
  onRefresh,
  onSetDefault,
  deleting = false,
  settingDefault = false,
  className,
}: ConnectionCardProps) {
  return (
    <Card className={cn(className)}>
      <CardContent className="flex items-center gap-4 py-4">
        <ProviderLogo
          provider={connection.provider_config_key}
          displayName={connection.display_name || undefined}
          logoUrl={providerLogo}
          size="lg"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium truncate">
              {connection.display_name || connection.alias}
            </h3>
            <ConnectionStatus status={connection.status} />
            {connection.is_default && (
              <Badge variant="secondary" className="text-xs">
                <Star className="size-3 mr-1 fill-current" />
                Default
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {connection.provider_config_key} &bull; Connected {formatRelativeTime(connection.created_at)}
          </p>
        </div>
        <CardAction>
          <div className="flex items-center gap-1">
            {onSetDefault && !connection.is_default && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onSetDefault}
                disabled={settingDefault}
                title="Set as default"
              >
                <Star className="size-4" />
              </Button>
            )}
            {onRefresh && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onRefresh}
                title="Refresh connection"
              >
                <RefreshCw className="size-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onDelete}
                disabled={deleting}
                title="Delete connection"
              >
                <Trash2 className="size-4 text-destructive" />
              </Button>
            )}
          </div>
        </CardAction>
      </CardContent>
    </Card>
  );
}
